import {ColorSchemeType} from 'platform/components';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {useGetInterestV2Query} from '@dms/api/finance';
import {useChangeInterestLeadQualityMutation, useGetLeadQualityOptionsQuery} from '@dms/api/sales';
import {LeadQualityEnum} from '@dms/api/shared';
import {EntityHeaderDropdownFlag, handleApiError, useInterestEditPermissions} from '@dms/shared';

import {buildArray, Nullish} from 'shared/web';

const getLeadQualityColorScheme = (value: string | Nullish) =>
  match<string | Nullish, ColorSchemeType>(value)
    .with('blue', always('blue'))
    .with('yellow', always('yellow'))
    .with('red', always('red'))
    .with('gray', always('neutral'))
    .otherwise(always('neutral'));

export function useInterestDropdownFlags(interestId: string) {
  const {data: interest} = useGetInterestV2Query({id: interestId});
  const {data: leadQualityOptions} = useGetLeadQualityOptionsQuery();
  const [changeInterestLeadQuality, {isLoading: isChangeLeadQualityLoading}] =
    useChangeInterestLeadQualityMutation();
  const {hasChangeLeadQualityInterestPermission} = useInterestEditPermissions({interestId});

  const onChangeInterestLeadQuality = (leadQuality: LeadQualityEnum) => {
    changeInterestLeadQuality({
      interestId,
      changeLeadQualityRequestBody: {
        leadQuality,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const currentLeadQuality = leadQualityOptions?.find(
    (option) => option.value === interest?.leadQuality
  );

  const filteredOptions =
    leadQualityOptions?.filter((option) => option.value !== interest?.leadQuality) ?? [];

  return buildArray<EntityHeaderDropdownFlag>().add({
    label: currentLeadQuality?.label ?? '',
    colorScheme: getLeadQualityColorScheme(currentLeadQuality?.color),
    isDisabled: !hasChangeLeadQualityInterestPermission || isChangeLeadQualityLoading,
    isLoading: isChangeLeadQualityLoading,
    dropdownItems: filteredOptions.map((option) => ({
      label: option.label,
      onClick: () => {
        onChangeInterestLeadQuality(option.value as LeadQualityEnum);
      },
      flag: {
        label: option.label,
        colorScheme: getLeadQualityColorScheme(option?.color),
        isSubtle: false,
      },
    })),
  });
}
